import { Modal, Stack, Text, Group, Button, Badge } from '@mantine/core';
import { Encounter } from '@medplum/fhirtypes';
import { FC, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@medplum/core';

interface CompleteEncounterModalProps {
  opened: boolean;
  onClose: () => void;
  encounter: Encounter;
  onEncounterUpdated?: (encounter: Encounter) => void;
}

export const CompleteEncounterModal: FC<CompleteEncounterModalProps> = ({
  opened,
  onClose,
  encounter,
  onEncounterUpdated,
}) => {
  const { t } = useTranslation(['encounters']);
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    setLoading(true);
    try {
      const updated: Encounter = {
        ...encounter,
        status: 'finished',
        period: {
          ...encounter.period,
          end: new Date().toISOString(),
        },
      };
      const result = await medplum.updateResource(updated);

      notifications.show({
        icon: <IconCircleCheck />,
        title: t('success', { ns: 'encounters' }),
        message: t('completed', { ns: 'encounters' }),
      });

      onEncounterUpdated?.(result);
      onClose(); // Close modal after success
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: t('error', { ns: 'encounters' }),
        message:
          error instanceof Error
            ? error.message
            : t('completeError', { ns: 'encounters' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const encounterType = encounter?.type?.[0]?.coding?.[0]?.display ?? '-';
  const startDateTime = formatDateTime(encounter?.period?.start);
  const practitioner = encounter?.participant?.[0]?.individual?.display;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('complete.title', { ns: 'encounters' })}
    >
      <Stack>
        <Text>{t('complete.confirmation', { ns: 'encounters' })}</Text>

        <Stack gap="xs" mt="sm" pl="sm">
          <Text size="sm">
            <strong>{t('type', { ns: 'encounters' })}:</strong>{' '}
            <Badge color="blue" variant="light">
              {encounterType}
            </Badge>
          </Text>

          <Text size="sm">
            <strong>{t('dateTime', { ns: 'encounters' })}:</strong>{' '}
            {startDateTime}
          </Text>

          {practitioner && (
            <Text size="sm">
              <strong>{t('practitioner', { ns: 'encounters' })}:</strong>{' '}
              {practitioner}
            </Text>
          )}
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            {t('buttons.cancel', { ns: 'encounters' })}
          </Button>
          <Button color="green" onClick={handleComplete} loading={loading}>
            {t('complete.button', { ns: 'encounters' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
